import { LocalizedText } from "@/i18n/LocalizedText";
import * as React from "react";
import {
  Area,
  AreaChart,
  Tooltip as RechartsTooltip,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from "recharts";
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Spinner } from "./ui/spinner";
import { canonicalizeDomain } from "@/utils/domain";
export const description = "An interactive area chart";
import noData from "../assets/no-data.svg";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { Separator } from "@radix-ui/react-separator";
import { ChartColumnIncreasing, ChartSpline } from "lucide-react";
import { useState } from "react";

export interface IChartDataItem {
  date: string;
  [key: string]: string | number;
}

export function ChartAreaInteractive({
  showDescription = true,
  chartData = [],
  currentBrand,
  seriesOrder,
  barValuesBySeries,
  windowDays,
  loading = false,
}: {
  showDescription?: boolean;
  loading?: boolean;
  timeRange: string;
  setTimeRange: (time: string) => void;
  chartData: IChartDataItem[];
  currentBrand: string;
  seriesOrder?: string[];
  barValuesBySeries?: Record<string, number>;
  windowDays?: number;
}) {
  const [competitorsOn, setCompetitorsOn] = React.useState(true);
  const [activeTab, setActiveTab] = React.useState("bars");

  // // Extract all unique brand keys (excluding 'date')
  // const brandKeys = Array.from(
  //   chartData.reduce((set, item) => {
  //     Object.keys(item).forEach((key) => {
  //       if (key !== "date") set.add(key);
  //     });
  //     return set;
  //   }, new Set<string>()),
  // );

  // Helper to generate a random color
  function getRandomColor() {
    return [
      "#e22121ff",
      "#FFC53D",
      "#22C55E",
      "#3DB9CF",
      "#6E56CF",
      "#FFD3D5",
      "#92487A",
      "#22C55E",
      "#3A6F43",
      "#fc71feff",
      "#FF5E00",
      "#30f9c4ff",
    ];
  }

  // Sort chartData by date ascending (earlier to later)
  const sortedByDate = [...chartData]
    .map((row) => {
      const normalized: Record<string, number | string> = { date: row.date };
      Object.entries(row).forEach(([key, value]) => {
        if (key === "date") return;
        const num = Number(value);
        if (Number.isFinite(num)) {
          normalized[key] = num;
        }
      });
      return normalized as IChartDataItem;
    })
    .sort((a, b) => a.date.localeCompare(b.date));

  // Keep all brands for each date (no top-N truncation)
  const processedData = sortedByDate.map((item) => {
    const entries = Object.entries(item).filter(([key]) => key !== "date");
    return { date: item.date, ...Object.fromEntries(entries) };
  });

  // If competitorsOn is false, show only currentBrand for each date
  const displayData = competitorsOn
    ? processedData
    : processedData.map((item: any) => {
        if (item[currentBrand] !== undefined) {
          return { date: item.date, [currentBrand]: item[currentBrand] };
        } else {
          return { date: item.date };
        }
      });

  // Get all brands actually present in displayData
  const presentBrandKeys = Array.from(
    displayData.reduce((set, item) => {
      Object.keys(item).forEach((key) => {
        if (key !== "date") set.add(key);
      });
      return set;
    }, new Set<string>()),
  );

  const orderedBrandKeys = React.useMemo(() => {
    if (!seriesOrder?.length) return presentBrandKeys;
    const orderMap = new Map<string, number>();
    seriesOrder.forEach((key, index) => {
      if (typeof key === "string") {
        orderMap.set(key, index);
      }
    });
    const originalIndex = new Map<string, number>();
    presentBrandKeys.forEach((key, index) => {
      originalIndex.set(key, index);
    });
    return [...presentBrandKeys].sort((a, b) => {
      const aOrder = orderMap.get(a);
      const bOrder = orderMap.get(b);
      const aHasOrder = aOrder !== undefined;
      const bHasOrder = bOrder !== undefined;
      if (aHasOrder && bHasOrder) {
        return aOrder - bOrder;
      }
      if (aHasOrder) return -1;
      if (bHasOrder) return 1;
      return (originalIndex.get(a) ?? 0) - (originalIndex.get(b) ?? 0);
    });
  }, [presentBrandKeys, seriesOrder]);

  // Build chartConfig only for present brands (no gradients)
  const chartConfig = React.useMemo(
    () =>
      orderedBrandKeys.reduce<Record<string, { label: string; color: string }>>(
        (acc, brand, index) => {
          acc[brand] = { label: brand, color: getRandomColor()[index] };
          return acc;
        },
        {},
      ),
    [orderedBrandKeys.join(",")],
  );

  const formatDate = (value: unknown) => {
    const date = new Date(String(value));
    return isNaN(date.getTime())
      ? ""
      : date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  };

  const tooltipContent = React.useCallback(
    (props: any) => {
      const orderMap = new Map<string, number>();
      if (seriesOrder?.length) {
        seriesOrder.forEach((key, index) => {
          if (typeof key === "string") {
            orderMap.set(key, index);
          }
        });
      }
      const sortedPayload = Array.isArray(props?.payload)
        ? [...props.payload].sort((a, b) => {
            const aValue = Number(a?.value);
            const bValue = Number(b?.value);
            const aScore = Number.isFinite(aValue) ? aValue : -Infinity;
            const bScore = Number.isFinite(bValue) ? bValue : -Infinity;
            const aName = String(a?.name ?? a?.dataKey ?? "");
            const bName = String(b?.name ?? b?.dataKey ?? "");
            const aOrder = orderMap.get(aName);
            const bOrder = orderMap.get(bName);
            const aHasOrder = aOrder !== undefined;
            const bHasOrder = bOrder !== undefined;
            if (aHasOrder && bHasOrder) {
              if (aOrder !== bOrder) return aOrder - bOrder;
            } else if (aHasOrder) {
              return -1;
            } else if (bHasOrder) {
              return 1;
            }
            if (bScore !== aScore) {
              return bScore - aScore;
            }
            return aName.localeCompare(bName);
          })
        : props?.payload;

      return (
        <ChartTooltipContent
          {...props}
          payload={sortedPayload}
          className="w-[220px] border-white/15 bg-black text-white"
          labelFormatter={formatDate}
          indicator="dot"
          formatter={(value: number, name: string, item: any) => {
            const rawName =
              typeof name === "string" ? name.trim() : String(name);
            const domain = canonicalizeDomain(rawName) || rawName;
            const color = item?.color || item?.stroke;
            const displayValue =
              typeof value === "number" ? `${Math.round(value)}%` : value;
            const faviconUrl = domain
              ? `https://t1.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&url=http://${domain}&size=64`
              : "";
            return (
              <div className="flex w-full items-center justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  <span
                    className="h-2.5 w-2.5 rounded-[2px] shrink-0"
                    style={{ backgroundColor: color }}
                  />
                  {faviconUrl ? (
                    <img
                      src={faviconUrl}
                      alt={domain}
                      className="h-4 w-4 rounded-sm shrink-0"
                    />
                  ) : null}
                  <span
                    className={`truncate text-xs text-white ${domain === currentBrand ? "font-semibold" : ""}`}
                  >
                    {domain}
                  </span>
                </div>
                <span className="font-mono text-xs font-medium tabular-nums text-white">
                  {displayValue}
                </span>
              </div>
            );
          }}
        />
      );
    },
    [currentBrand, seriesOrder],
  );
  const chartDataAverage = React.useMemo(() => {
    return orderedBrandKeys
      .map((brand, index) => {
        const overrideValue = barValuesBySeries?.[brand];
        const hasOverride = Number.isFinite(overrideValue);
        const values = displayData.map(
          (d) => (d as { [key: string]: any })[brand] ?? 0,
        );
        const avg = values.length
          ? values.reduce((sum, val) => sum + val, 0) / values.length
          : 0;
        const sourceValue = hasOverride ? Number(overrideValue) : avg;
        const rounded = Math.round(Math.min(100, Math.max(0, sourceValue)));

        return {
          type: brand,
          percentage: rounded,
          color: getRandomColor()[index % getRandomColor().length],
        };
      })
      .sort((a, b) => b.percentage - a.percentage);
  }, [orderedBrandKeys, displayData, barValuesBySeries]);

  const [animatedData, setAnimatedData] = useState(chartDataAverage);
  const tooltipWindowDays =
    Number.isFinite(windowDays) && Number(windowDays) > 0
      ? Math.round(Number(windowDays))
      : displayData.length;
  React.useEffect(() => {
    const isSame =
      animatedData.length === chartDataAverage.length &&
      animatedData.every(
        (a, i) =>
          a.type === chartDataAverage[i].type &&
          a.percentage === chartDataAverage[i].percentage,
      );

    if (!isSame) {
      const timer = setTimeout(() => setAnimatedData(chartDataAverage), 0);
      return () => clearTimeout(timer);
    }
  }, [chartDataAverage, animatedData]);

  return (
    <Card className="@container/card">
      {loading ? (
        <div className="h-full relative flex flex-col gap-2">
          <div className="z-10 gap-1 flex-col flex justify-center items-center w-full h-full">
            <div className="flex gap-2 items-center flex-col p-2 bg-muted rounded-md">
              <Spinner className="text-[#FF5E00]" variant="circle"></Spinner>
            </div>
            <div>
              <LocalizedText id="legacy.processing.3jbdvy" />
            </div>
          </div>
        </div>
      ) : !chartData.length ? (
        <div className="flex flex-col gap-2 items-center my-5">
          <div>
            <img src={noData}></img>
          </div>
          <div className="text-lg text-foreground font-medium">
            <LocalizedText id="legacy.no.data.1dp9hn8" />
          </div>
          <div className="text-center text-sm text-muted-foreground max-w-[288px] whitespace-break-spaces">
            <LocalizedText id="legacy.unfortunately.we.havent.received.any.data.if35xt" />
          </div>
        </div>
      ) : (
        <>
          <CardHeader>
            <CardTitle>
              <LocalizedText id="legacy.visibility.score.1nllkiz" />
            </CardTitle>
            <CardDescription>
              {showDescription && (
                <span className="@[540px]/card:block">
                  <LocalizedText id="legacy.percentage.of.prompts.where.your.brand.appears.in.dajnya" />
                </span>
              )}
            </CardDescription>
            <CardAction className="flex gap-2">
              <div className="flex items-center space-x-2">
                <Switch
                  checked={competitorsOn}
                  onCheckedChange={setCompetitorsOn}
                  className={`${competitorsOn && "data-[state=checked]:bg-[#FF5E00]"}`}
                  id="notifications"
                />
                <Label htmlFor="notifications">
                  <LocalizedText id="legacy.competitors.94sz4m" />
                </Label>
              </div>
              <Separator
                orientation="vertical"
                className="bg-border shrink-0 data-[orientation=horizontal]:h-px data-[orientation=horizontal]:w-full data-[orientation=vertical]:w-px mr-2 data-[orientation=vertical]:h-4"
              />
              <div>
                <Tabs value={activeTab} onValueChange={setActiveTab}>
                  <TabsList>
                    <TabsTrigger className="cursor-pointer" value={"bars"}>
                      <ChartColumnIncreasing />
                    </TabsTrigger>
                    <TabsTrigger className="cursor-pointer" value={"chars"}>
                      <ChartSpline />
                    </TabsTrigger>
                  </TabsList>
                </Tabs>
              </div>
            </CardAction>
          </CardHeader>
          <CardContent className="px-2 pt-4 sm:px-6 sm:pt-6">
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsContent value="bars">
                <div className="w-full h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={animatedData}
                      margin={{ top: 20, right: 10, left: 10, bottom: 20 }}
                    >
                      <CartesianGrid vertical={false} horizontal={false} />
                      <XAxis
                        dataKey="type"
                        tickLine={false}
                        interval={0}
                        tickFormatter={(val) => `${val}%`}
                        axisLine={false}
                        tick={({ x, y, payload }) => {
                          const brand = payload.value;
                          return (
                            <g transform={`translate(${x},${y + 17})`}>
                              <>
                                <rect
                                  x={-18}
                                  y={-37 / 2}
                                  width={36}
                                  height={32}
                                  rx={8}
                                  ry={8}
                                  fill="#F5F5F5"
                                />

                                <rect
                                  x={-18}
                                  y={-70 / 2}
                                  width={36}
                                  height={40}
                                  fill="#F5F5F5"
                                />
                              </>
                              <defs>
                                <mask id={`mask-${payload.value}`}>
                                  <rect
                                    x={-12}
                                    y={-18}
                                    width={24}
                                    height={24}
                                    rx={6}
                                    ry={6}
                                    fill="white"
                                  />
                                </mask>
                              </defs>
                              <image
                                href={`https://www.google.com/s2/favicons?domain=${encodeURIComponent(payload.value || brand)}&sz=64`}
                                x={-12.2}
                                y={-18}
                                width={24}
                                height={24}
                                className="rounded-lg"
                                mask={`url(#mask-${payload.value})`}
                              />
                            </g>
                          );
                        }}
                      />
                      <YAxis
                        tickFormatter={(val) => `${val}%`}
                        tickLine={false}
                        axisLine={false}
                      />

                      <RechartsTooltip
                        cursor={{ fill: "rgba(255,255,255,0.08)" }}
                        content={({ active, payload }) => {
                          const item = payload?.[0]?.payload as
                            | {
                                type: any;
                                percentage: number;
                              }
                            | undefined;
                          if (!active || !item) return null;
                          return (
                            <div className="rounded-lg border border-white/10 bg-black px-3 py-2 text-xs shadow-xl">
                              <div className="mb-2 flex justify-between text-xs items-center gap-2 text-white font-medium">
                                <div>
                                  {tooltipWindowDays}{" "}
                                  {tooltipWindowDays > 1 ? "days" : "day"}
                                </div>
                                <div className="text-[#A3A3A3]">
                                  <LocalizedText id="legacy.visibility.s925mr" />
                                </div>
                              </div>
                              <div className="border-t border-[#A3A3A3] pt-2 items-center flex gap-2 mt-1 text-zinc-300 truncate">
                                <div
                                  className="rounded-md w-[3px] h-[15px]"
                                  style={{
                                    backgroundColor:
                                      chartConfig[item.type]?.color,
                                  }}
                                ></div>
                                <img
                                  className="w-4 rounded-md bg-white border"
                                  src={`https://t1.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&url=http://${canonicalizeDomain(item.type) || ""}&size=64`}
                                  alt="logo"
                                />
                                <div className="text-[#A3A3A3] text-xs">
                                  {item.type}
                                </div>
                                <div className="text-[#FAFAFA] text-sm">
                                  {item.percentage}%
                                </div>
                              </div>
                            </div>
                          );
                        }}
                      />

                      <Bar
                        key={chartDataAverage.length}
                        dataKey="percentage"
                        isAnimationActive={true}
                        animationDuration={600}
                        radius={[4, 4, 4, 4]}
                        barSize={36}
                        background={(props: any) => {
                          const extraTop = 10;
                          return (
                            <rect
                              x={props.x}
                              y={props.y - extraTop}
                              width={props.width}
                              height={props.height + extraTop}
                              rx={8}
                              ry={8}
                              fill="#F5F5F5"
                            />
                          );
                        }}
                      >
                        {chartDataAverage.map((entry, index) => (
                          <Cell key={index} fill={entry.color || "#F5F5F5"} />
                        ))}
                        <LabelList
                          dataKey="percentage"
                          position="top"
                          formatter={(val) => `${val}%`}
                          style={{ fontSize: 14, fill: "#000" }}
                        />
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </TabsContent>
              <TabsContent value="chars">
                <ChartContainer
                  config={chartConfig}
                  className="aspect-auto h-[250px] w-full"
                >
                  <AreaChart data={displayData}>
                    <CartesianGrid vertical={false} />
                    <XAxis
                      dataKey="date"
                      tickLine={false}
                      axisLine={false}
                      tickMargin={8}
                      minTickGap={32}
                      tickFormatter={formatDate}
                    />
                    <YAxis
                      tickLine={false}
                      tickMargin={10}
                      axisLine={false}
                      tickFormatter={(value) => `${value}%`}
                      domain={[0, 100]}
                    />
                    <ChartTooltip cursor={false} content={tooltipContent} />
                    {orderedBrandKeys.map((brand) => (
                      <Area
                        key={brand}
                        dataKey={brand}
                        stroke={chartConfig[brand]?.color}
                        type="monotone"
                        fill="none"
                        strokeWidth={2}
                        name={brand}
                        isAnimationActive={true}
                        animationDuration={600}
                        animationEasing="ease-in-out"
                      />
                    ))}
                  </AreaChart>
                </ChartContainer>
              </TabsContent>
            </Tabs>
          </CardContent>
        </>
      )}
    </Card>
  );
}
